import React from 'react'
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { useRegister } from "@/hooks/mutations/auth";
import { authService, slotService } from "@/services";
import useAuth from "@/hooks/stores/useAuth";
import useBooking from "@/hooks/stores/useBooking";
import { UserPlus } from "lucide-react";
import { motion } from "framer-motion";

//test
export const Signup = () => {
    const navigate = useNavigate();
    const { setUserRole } = useAuth();
    const { bookingData, clearBookingData, saveBookingData } = useBooking();

    const registerMutation = useRegister({
      onSuccess: async (response) => {
        console.log("API Response:", response);

        // authService.register returns different structure
        const { user } = response;

        if (!user) {
          toast.error("Failed to retrieve user information.");
          return;
        }

        console.log("User role:", user.roles?.[0]?.title);
        
        // Set the user role since it's required for routing
        if (user.roles && user.roles.length > 0) {
        setUserRole(user.roles[0].title);
        }
        
        // If there's booking data, make the slot booking API call now that user is authenticated
        if (bookingData) {
          try {
            // Add a small delay to ensure token is properly set in cookies
            await new Promise(resolve => setTimeout(resolve, 100));
            
            console.log("Making slot booking with data:", bookingData);
            
            let slotResponse;
            
            // Check if this is a remote booking (assisted recording)
            if (bookingData.bookingType === "remote") {
              // Prepare data for remote slot booking API
              const remoteSlotData = {
                slot_date: bookingData.slot_date,
                slot_time: bookingData.slot_time,
                duration: bookingData.duration,
                location: bookingData.location,
                converted_time: bookingData.converted_time,
                preferred_platform: bookingData.preferred_platform,
                interviewer_id: bookingData.interviewer_id
              };
              
              console.log("Making remote slot booking with data:", remoteSlotData);
              slotResponse = await slotService.selectRemoteSlot(remoteSlotData);
              console.log("Remote slot booking successful:", slotResponse);
            } else {
              // Regular in-person booking
              console.log("Making regular slot booking with data:", bookingData);
              slotResponse = await slotService.selectSlot(bookingData);
              console.log("Regular slot booking successful:", slotResponse);
            }
            
            // Save the slot information from API response to booking context
            if (slotResponse && slotResponse.slot) {
              const updatedBookingData = {
                ...bookingData,
                slotInfo: slotResponse.slot,
                bookingId: slotResponse.slot.slot_id || slotResponse.slot.id
              };
              saveBookingData(updatedBookingData);
              console.log("Updated booking data with slot info:", updatedBookingData);
            }
            
            // Navigate to paywall
            navigate("/paywall");
          } catch (error) {
            console.error("Slot booking failed:", error);
            
            // Enhanced error handling for remote bookings
            let errorMessage = "Account created but booking failed. Please contact support.";
            
            if (error.response && error.response.data) {
              const errorData = error.response.data;
              
              if (typeof errorData.error === 'string') {
                errorMessage = `Booking failed: ${errorData.error}`;
              } else if (typeof errorData.message === 'string') {
                errorMessage = `Booking failed: ${errorData.message}`;
              } else if (typeof errorData.error === 'object') {
                // Handle validation errors
                const errorMessages = Object.values(errorData.error).flat();
                errorMessage = `Booking failed: ${errorMessages.join(', ')}`;
              }
            } else if (error.message) {
              errorMessage = `Booking failed: ${error.message}`;
            }
            
            toast.error(errorMessage);
            
            // Still navigate to paywall even if booking fails
            navigate("/paywall");
          }
        } else {
          // No booking data, just navigate to welcome video (normal flow)
          navigate("/welcome-video");
        }
      },
      onError: (error) => {
        console.error("Error during signup:", error);
        // Check if the error response has a message
        if (error.response && error.response.data) {
          // If the error is an object, extract the message
          const errorMessages = error.response.data.error;

          // If errorMessages is an object, convert it to a string
          if (typeof errorMessages === 'object') {
            // Join the error messages if it's an array or extract the first message
            const message = Array.isArray(errorMessages) 
              ? errorMessages.join(', ') 
              : Object.values(errorMessages).join(', '); // Join object values if it's an object
            toast.error(message);
          } else {
            toast.error(errorMessages); // If it's a string, show it directly
          }
        } else {
          toast.error(error.message || "Signup failed. Please try again.");
        }
      },
    });

    const validationSchema = Yup.object({
        name: Yup.string()
          .required("Name is required"),
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
        password: Yup.string()
          .min(6, "Password must be at least 6 characters")
          .required("Password is required"),
        password_confirmation: Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .required("Confirm Password is required"),
      });
    
      const formik = useFormik({
        initialValues: {
          name: "",
          email: "",
          password: "",
          password_confirmation: ""
        },
        validationSchema,
        onSubmit: async (values) => {
          try {
            // First logout any existing user
            // await authService.logout();
            
            // Then proceed with user registration
            const userData = {
              name: values.name.trim(),
              email: values.email.trim(),
              password: values.password.trim(),
              password_confirmation: values.password_confirmation.trim(),
              roles: [2] // Default User role ID
            };
            console.log("User Data:", userData);
            registerMutation.mutate(userData);
          } catch (error) {
            console.error("Error during signup process:", error);
            toast.error("An error occurred during the signup process. Please try again.");
          }
        },
    });



  return (
    <div className="min-h-screen bg-background flex items-center pt-5 pb-2">
      <div className="max-w-md mx-auto px-4 w-full">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-secondary rounded-xl shadow-lg p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <UserPlus className="text-white text-2xl" />
            </div>
            <h1 className="text-base font-semibold text-foreground mb-2">Create Your Account</h1>
            <p className="text-base font-normal text-foreground">Join Postscrypt to preserve your legacy</p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="space-y-3">
              <div>
                <label className="block text-base font-semibold text-foreground mb-1">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                  placeholder="John Doe" 
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-base font-semibold text-foreground mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                  placeholder="your@email.com" 
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-[18px] font-semibold text-foreground mb-1">Password</label>
                <input
                  type="password"
                  name="password"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                  placeholder="••••••••" 
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-[18px] font-semibold text-foreground mb-1">Confirm Password</label>
                <input
                  type="password"
                  name="password_confirmation"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                  placeholder="••••••••" 
                  value={formik.values.password_confirmation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.password_confirmation && formik.errors.password_confirmation && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password_confirmation}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={registerMutation.isPending}
                className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors"
              >
                {registerMutation.isPending ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          {/* <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-primary hover:text-primary/80 font-medium"
              >
                Sign in
              </button>
            </p>
          </div> */}
        </motion.div>
      </div>
    </div>
  )
}
